<script setup lang="ts">
import type { ArticleContent } from '~/models/Content/ArticleContent'
import { computed } from 'vue'
import { firstOf } from '~/utils/queryUtils'
import { useMedia } from '~/composables/useMedia'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import FullscreenContent from '~/components/utils/FullscreenContent.vue'
import CopyrightButton from '~/components/utils/CopyrightButton.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import Image from '~/components/media/Image.vue'

const props = defineProps<{
  articleContent: ArticleContent
}>()

const mediaContentId = computed(() => [Number(props.articleContent.svg?.destinationContentId ?? props.articleContent.image?.destinationContentId ?? 0)])
const { data: media, isFetching } = firstOf(useMedia(mediaContentId))
const reverse = computed(() => props.articleContent.reverse)
</script>

<template>
  <section class="col-[media] flex items-center gap-(--col-gap) max-md:flex-col">
    <AuSkeleton
      v-if="isFetching"
      class="aspect-square !size-[calc(var(--col-width)*3+var(--col-gap)*2)] flex-none !rounded-full"
      :class="{
        'md:order-2': !reverse,
      }"
    />
    <div
      v-else-if="media"
      class="relative size-[calc(var(--col-width)*3+var(--col-gap)*2)] flex-none"
      :class="{
        'md:order-2': !reverse,
      }"
    >
      <FullscreenContent>
        <template #default="{ isFullscreen }">
          <Image
            v-if="media"
            :content="media"
            class="col-start-1 row-start-1 aspect-square !h-full max-h-[90lvh]"
            :class="{
              'place-self-center bg-(--theme-5) object-contain p-4': isFullscreen,
              'rounded-full object-cover': !isFullscreen,
            }"
            :width="isFullscreen ? '100vw' : '400px'"
            :use-focal-point="!isFullscreen"
          />
          <CopyrightButton
            v-if="'copyright' in media && media.copyright"
            :copyright="media.copyright"
            class="!absolute right-0 bottom-0"
          />
        </template>
      </FullscreenContent>
    </div>
    <div class="au-prose au-subtitle font-semibold">
      <RichTextRenderer :text="articleContent.body ?? ''" />
    </div>
  </section>
</template>
